import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    Signal,
} from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButton } from "@angular/material/button";
import { MatChipGrid, MatChipRow, MatChipInput, MatChipRemove } from "@angular/material/chips";
import { MatOption } from "@angular/material/core";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatInput } from "@angular/material/input";
import { MatSelect } from "@angular/material/select";
import { EnumKeyValuePipe } from "../../../pipes/enumKeyValue.pipe";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Nullable } from "@playcharge/common/models/backend/nullable";
import { QuestConditionType } from "@playcharge/common/models/backend/quests/quest-condition-type";
import { QuestTriggerType } from "@playcharge/common/models/backend/quests/quest-trigger-type";
import { QuestStore } from "../../../stores/quests/quest.store";
import { QuestsStore } from "../../../stores/quests/quests.store";
import { QuestBaseComponent } from "../quest-base.component";
import { QuestViewModel } from "@playcharge/common/models/backend/quests/backoffice/quest-view-model";
import { QuestConditionForm, QuestForm } from "../../../models/quests/quest-form";
import { MatCheckbox } from "@angular/material/checkbox";
import { QuestEditModel } from "@playcharge/common/models/backend/quests/backoffice/quest-edit-model";
import { QuestCreateModel } from "@playcharge/common/models/backend/quests/backoffice/quest-create-model";
import { Observable, tap } from "rxjs";
import { ComponentRoutes } from "../../../models/constants/component-routes";
import { NoScrollDirective } from "../../../directives/noScrollDirective";

@Component({
    selector: "app-quest",
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatSelect,
        MatOption,
        MatChipGrid,
        MatChipRow,
        MatChipInput,
        MatChipRemove,
        MatIcon,
        MatCheckbox,
        MatButton,
        EnumKeyValuePipe,
        NoScrollDirective,
    ],
    providers: [QuestStore],
    templateUrl: "./quest.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestComponent extends QuestBaseComponent {
    private _questsStore: QuestsStore = inject(QuestsStore);
    private _questStore: QuestStore = inject(QuestStore);

    public isEditMode: Signal<boolean> = computed<boolean>(() => !!this._questStore.questId());

    public form: FormGroup<QuestForm> = new FormGroup<QuestForm>({
        templateId: new FormControl<Nullable<number>>({ value: null, disabled: this.isEditMode() }),
        name: new FormControl<Nullable<string>>(null),
        triggerType: new FormControl<QuestTriggerType>(
            { value: QuestTriggerType.Registered, disabled: this.isEditMode() },
            {
                nonNullable: true,
                validators: [Validators.required],
            },
        ),
        scheduleId: new FormControl<Nullable<number>>({ value: null, disabled: this.isEditMode() }),
        condition: new FormGroup<QuestConditionForm>({
            conditionType: new FormControl<QuestConditionType>(
                { value: null!, disabled: this.isEditMode() },
                {
                    nonNullable: true,
                    validators: [Validators.required],
                },
            ),
            count: new FormControl<number>(
                { value: 0, disabled: this.isEditMode() },
                {
                    nonNullable: true,
                    validators: [Validators.required],
                },
            ),
            amount: new FormControl<number>(
                { value: 0, disabled: this.isEditMode() },
                {
                    nonNullable: true,
                    validators: [Validators.required],
                },
            ),
            goalIds: new FormControl<Nullable<number[]>>(null),
        }),
        offerId: new FormControl<Nullable<number>>({ value: null, disabled: this.isEditMode() }),
        moneyReward: new FormControl<number>(
            { value: 0, disabled: this.isEditMode() },
            {
                nonNullable: true,
                validators: [Validators.required],
            },
        ),
        expReward: new FormControl<number>(
            { value: 0, disabled: this.isEditMode() },
            {
                nonNullable: true,
                validators: [Validators.required],
            },
        ),
        isActive: new FormControl<boolean>(true, { nonNullable: true }),
    });

    constructor() {
        super();

        effect(() => this.questChangeEffectFn());

        this.form.controls.triggerType.valueChanges
            .pipe(takeUntilDestroyed())
            .subscribe((value) => this.onTriggerTypeChanged(value));

        this.form.controls.templateId.valueChanges
            .pipe(takeUntilDestroyed())
            .subscribe((value) => this.onTemplateChanged(value));
    }

    public createOrUpdateQuest(): void {
        const formValue = this.form.getRawValue();

        let request: Observable<QuestViewModel>;

        if (this._questStore.questId() === 0) {
            const createModel: QuestCreateModel = {
                name: formValue.name,
                triggerType: formValue.triggerType,
                scheduleId: formValue.scheduleId,
                condition: formValue.condition,
                offerId: formValue.offerId,
                moneyReward: formValue.moneyReward,
                expReward: formValue.expReward,
                isActive: formValue.isActive,
            };

            request = this._questsStore.createQuest(createModel).pipe(
                tap((quest) => {
                    this.router.navigateByUrl(
                        ComponentRoutes.quests.quest.replace(":questId", quest.id.toString()),
                    );
                }),
            );
        } else {
            const editModel: QuestEditModel = {
                id: this._questStore.questId(),
                name: formValue.name,
                isActive: formValue.isActive,
            };

            request = this._questsStore.editQuest(editModel);
        }

        request.subscribe({
            next: () => this.toastrService.success("Done"),
            error: (error) =>
                this.toastrService.error(error?.error?.description ?? "Internal Error"),
        });
    }

    private questChangeEffectFn(): void {
        const quest: Nullable<QuestViewModel> = this._questStore.quest();

        if (quest) {
            this.form.patchValue(quest);
        }
    }

    public override get goalIdsControl(): FormControl<Nullable<number[]>> {
        return this.form.controls.condition.controls.goalIds;
    }

    private onTriggerTypeChanged(value: QuestTriggerType): void {
        if (value !== QuestTriggerType.Schedule) {
            this.form.patchValue({ scheduleId: null });
        }
    }

    private onTemplateChanged(value: Nullable<number>) {
        if (!value) {
            return;
        }

        const template = this.templatesStore.entityMap()[value];

        if (!template) {
            return;
        }

        this.form.patchValue(template);
    }
}
